import React from 'react';
import { Link } from 'react-router-dom';
import { getOptimizedImageUrl, generateSrcSet, handleImageError } from '../utils/imageOptimization';

// Eyebrow category labels mapping
const EYEBROW_LABELS = {
  'THE CUFF SET': 'EAR CUFF',
  'THE LAYER': 'NECKLACE',
  'THE BANGLE': 'BRACELET',
  'THE STACK': 'RING SET',
  'THE DROP': 'RING',
  'THE MELT': 'EARRINGS',
  'THE SIGNET': 'RING',
  'THE CUBAN': 'NECKLACE',
  'THE BAG': 'BAG',
  'THE WATCH': 'WATCH',
  'THE LIGHTER': 'OBJECT',
  // New products
  'THE TEAR': 'EARRINGS',
  'THE POUR': 'RING SET',
  'THE WAVE': 'RING',
  'THE CHAIN': 'RING',
  'THE LINE': 'BRACELET',
};

// Convert "THE BAG" to "The Bag"
const toSentenceCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ProductCard = ({ product }) => {
  const productUrl = product.customUrl || `/product/${product.id}`;
  
  // Use Shopify prices directly - no more hardcoded values
  const salePrice = Math.round(product.price);
  const compareAtPrice = product.compareAtPrice ? Math.round(product.compareAtPrice) : null;
  const hasSale = compareAtPrice && compareAtPrice > salePrice;
  
  // Calculate discount percentage
  const discountPercent = hasSale ? Math.round((compareAtPrice - salePrice) / compareAtPrice * 100) : 0;
  
  // Get eyebrow label
  const eyebrowLabel = EYEBROW_LABELS[product.name] || '';

  return (
    <Link 
      to={productUrl}
      className="group block relative"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container - Portrait ratio, no borders */}
      <div className="aspect-[4/5] overflow-hidden bg-plumo-bone relative">
        <img
          src={getOptimizedImageUrl(product.image, 800)}
          srcSet={generateSrcSet(product.image)}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 400px"
          alt={product.name}
          width="600"
          height="750"
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-[1.02] group-hover:opacity-95"
          loading="lazy"
          decoding="async"
          onError={handleImageError}
        />
        
        {/* Discount percentage badge */}
        {hasSale && discountPercent > 0 && (
          <span 
            className="absolute"
            style={{
              top: '12px',
              right: '12px',
              backgroundColor: '#E8F53A',
              color: '#0D0D0B',
              fontFamily: "'Space Mono', monospace",
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              padding: '4px 8px',
              borderRadius: '4px',
              lineHeight: 1,
            }}
          >
            –{discountPercent}%
          </span>
        )}
      </div>

      {/* Product Info - Clean, minimal */}
      <div className="pt-4">
        {/* Eyebrow category label */}
        {eyebrowLabel && (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '10px',
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#888',
              display: 'block',
              marginBottom: '4px',
            }}
          >
            {eyebrowLabel}
          </span>
        )}
        
        {/* Product name in Inter Bold (matching Nomès reference) */}
        <h3 
          className="product-card-name"
          style={{
            marginBottom: '8px',
          }}
        >
          {toSentenceCase(product.name)}
        </h3>
        
        {/* Prices from Shopify - 13px original / 15px sale */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
          {hasSale && (
            <span 
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '13px',
                fontWeight: 400,
                color: '#999',
                textDecoration: 'line-through',
                letterSpacing: '0.02em',
              }}
            >
              ${compareAtPrice}
            </span>
          )}
          <span 
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '15px',
              fontWeight: 700,
              color: '#0D0D0B',
              letterSpacing: '0.02em',
            }}
          >
            ${salePrice}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
